import { useEffect, useState, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, TrendingUp, TrendingDown, Briefcase } from 'lucide-react';
import axios from 'axios';
import { AppContext } from '@/App';

function Dashboard() {
  const { user, API } = useContext(AppContext);
  const [stats, setStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [profileRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/user/profile`),
        axios.get(`${API}/transactions/my`)
      ]);
      setStats(profileRes.data);
      setRecentTransactions(transactionsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user?.username}!</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-white shadow-lg" data-testid="balance-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Time Tokens</CardTitle>
            <Clock className="w-5 h-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600" data-testid="balance-value">{user?.time_tokens?.toFixed(1)}</div>
            <p className="text-xs text-gray-500 mt-1">Available balance</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg" data-testid="services-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">My Services</CardTitle>
            <Briefcase className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600" data-testid="services-count">{stats?.services_count || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Active listings</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg" data-testid="sent-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Services Requested</CardTitle>
            <TrendingDown className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600" data-testid="sent-count">{stats?.transactions_sent || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Total requests</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg" data-testid="received-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Services Provided</CardTitle>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600" data-testid="received-count">{stats?.transactions_received || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Total completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card className="bg-white shadow-lg" data-testid="recent-transactions-card">
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {recentTransactions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No transactions yet</p>
          ) : (
            <div className="space-y-4">
              {recentTransactions.map((tx, index) => (
                <div key={tx.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`transaction-${index}`}>
                  <div>
                    <p className="font-medium text-gray-800">{tx.service_title}</p>
                    <p className="text-sm text-gray-500">
                      {tx.from_user === user?.username ? `To: ${tx.to_user}` : `From: ${tx.from_user}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      tx.from_user === user?.username ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {tx.from_user === user?.username ? '-' : '+'}{tx.time_tokens} tokens
                    </p>
                    <p className="text-xs text-gray-500">{new Date(tx.timestamp).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Dashboard;